import { useAuth } from '../routes/auth/AuthContext'
import type { User, Event, Area, Shift } from '../data/types'

type ResourceType = 'event' | 'area' | 'shift' | 'allocation'
type Action = 'edit' | 'delete' | 'create'

interface UseResourcePermissionsReturn {
    canEditEvent: (event: Event | null) => boolean
    canDeleteEvent: (event: Event | null) => boolean
    canCreateEvent: () => boolean
    canEditArea: (area: Area | null, parentEvent?: Event | null) => boolean
    canDeleteArea: (area: Area | null) => boolean
    canCreateArea: (parentEvent?: Event | null) => boolean
    canEditShift: (shift: Shift | null, parentArea?: Area | null, grandparentEvent?: Event | null) => boolean
    canDeleteShift: (shift: Shift | null) => boolean
    canCreateShift: (parentArea?: Area | null, grandparentEvent?: Event | null) => boolean
    canViewAllocations: (shift: Shift | null, parentArea?: Area | null, grandparentEvent?: Event | null) => boolean
    canManageAllocations: (shift: Shift | null, parentArea?: Area | null, grandparentEvent?: Event | null) => boolean
    canEditDuty: (duty: any) => boolean
    canDeleteDuty: (duty: any) => boolean
    canCreateDuty: () => boolean
    canEditAccessColor: (accessColor: any) => boolean
    canDeleteAccessColor: (accessColor: any) => boolean
    canCreateAccessColor: () => boolean
    canEditTraining: (training: any) => boolean
    canDeleteTraining: (training: any) => boolean
    canCreateTraining: () => boolean
    canEditPreference: (preference: any) => boolean
    canDeletePreference: (preference: any) => boolean
    canCreatePreference: () => boolean
    canEditFlag: (flag: any) => boolean
    canDeleteFlag: (flag: any) => boolean
    canCreateFlag: () => boolean
    canEditAnnouncement: (announcement: any) => boolean
    canDeleteAnnouncement: (announcement: any) => boolean
    canCreateAnnouncement: () => boolean
    canExportTraining: () => boolean
    canManageVisibility: () => boolean
    canDeallocateDuplicate: () => boolean
    canDeallocateConflict: () => boolean
    canChangeAllocationStatus: () => boolean
    canEditGuideline: (guideline: any) => boolean
    canDeleteGuideline: (guideline: any) => boolean
    canCreateGuideline: () => boolean
}

/**
 * Hook for checking resource-based permissions
 * Implements hierarchical permission checking based on platform permissions and lead/deputy assignments
 */
export const useResourcePermissions = (): UseResourcePermissionsReturn => {
    const { user } = useAuth()

    const canDeallocateDuplicateRole = ["admin", "hr_admin"];

    const hasPermission = (permission: string): boolean => {
        return user?.permissions?.includes(permission) ?? false
    }

    const isLeadOrDeputy = (resource: Event | Area | Shift | null, volunteerId: number | undefined): boolean => {
        if (!resource || !volunteerId) return false

        // Check event_leads for events
        if ((resource as any).event_leads) {
            const eventLeads = (resource as any).event_leads
            if (Array.isArray(eventLeads) && eventLeads.some((el: any) => el.volunteer_csid === volunteerId)) {
                return true
            }
        }

        // Check lead_csid or deputy_lead_csid for areas and shifts
        if ((resource as any).lead_csid === volunteerId || (resource as any).deputy_lead_csid === volunteerId) {
            return true
        }

        // Check area_access for areas
        if ((resource as any).area_access) {
            const areaAccess = (resource as any).area_access
            if (Array.isArray(areaAccess) && areaAccess.some((aa: any) => aa.volunteer_csid === volunteerId)) {
                return true
            }
        }

        return false
    }

    const getVolunteerId = (): number | undefined => {
        return user?.volunteer?.csid
    }

    // Event permissions
    const canEditEvent = (event: Event | null): boolean => {
        if (!user) return false

        // Platform-wide permission
        if (hasPermission('manage_events')) return true

        // Hierarchical access requires manage_own_shifts permission
        if (!hasPermission('manage_own_shifts')) return false

        // Check if user is lead/deputy of this event
        return isLeadOrDeputy(event, getVolunteerId())
    }

    const canDeleteEvent = (event: Event | null): boolean => {
        // Delete requires platform permission only
        return hasPermission('delete_events')
    }

    const canCreateEvent = (): boolean => {
        // Creating events requires platform permission
        return hasPermission('create_events')
    }

    // Area permissions
    const canEditArea = (area: Area | null, parentEvent?: Event | null): boolean => {
        if (!user) return false

        // Platform-wide permission
        if (hasPermission('manage_areas')) return true

        // Hierarchical access requires manage_own_shifts permission
        if (!hasPermission('manage_own_shifts')) return false

        const volunteerId = getVolunteerId()

        // Check if user is lead/deputy of this area
        if (isLeadOrDeputy(area, volunteerId)) return true

        // Check if user is lead/deputy of parent event
        if (parentEvent && isLeadOrDeputy(parentEvent, volunteerId)) return true

        return false
    }

    const canDeleteArea = (area: Area | null): boolean => {
        // Delete requires platform permission only
        return hasPermission('delete_areas')
    }

    const canCreateArea = (parentEvent?: Event | null): boolean => {
        if (!user) return false

        // Platform-wide permission
        if (hasPermission('manage_areas')) return true

        // Hierarchical access requires manage_own_shifts permission
        if (!hasPermission('manage_own_shifts')) return false

        // Can create if user is lead/deputy of parent event
        return isLeadOrDeputy(parentEvent, getVolunteerId())
    }

    // Shift permissions
    const canEditShift = (shift: Shift | null, parentArea?: Area | null, grandparentEvent?: Event | null): boolean => {
        if (!user) return false

        // Platform-wide permission
        if (hasPermission('manage_shifts')) return true

        // Hierarchical access requires manage_own_shifts permission
        if (!hasPermission('manage_own_shifts')) return false

        const volunteerId = getVolunteerId()

        // Check if user is lead/deputy of this shift
        if (isLeadOrDeputy(shift, volunteerId)) return true

        // Check if user is lead/deputy of parent area
        if (parentArea && isLeadOrDeputy(parentArea, volunteerId)) return true

        // Check if user is lead/deputy of grandparent event
        if (grandparentEvent && isLeadOrDeputy(grandparentEvent, volunteerId)) return true

        return false
    }

    const canDeleteShift = (shift: Shift | null): boolean => {
        // Delete requires platform permission only
        return hasPermission('delete_shifts')
    }

    const canCreateShift = (parentArea?: Area | null, grandparentEvent?: Event | null): boolean => {
        if (!user) return false

        // Platform-wide permission
        if (hasPermission('manage_shifts')) return true

        // Hierarchical access requires manage_own_shifts permission
        if (!hasPermission('manage_own_shifts')) return false

        const volunteerId = getVolunteerId()

        // Can create if user is lead/deputy of parent area
        if (parentArea && isLeadOrDeputy(parentArea, volunteerId)) return true

        // Or lead/deputy of grandparent event
        if (grandparentEvent && isLeadOrDeputy(grandparentEvent, volunteerId)) return true

        return false
    }

    // Allocation permissions
    const canViewAllocations = (shift: Shift | null, parentArea?: Area | null, grandparentEvent?: Event | null): boolean => {
        if (!user) return false

        // Platform-wide permission
        if (hasPermission('view_allocations') || hasPermission('manage_allocations')) return true

        // If user has view_own_allocations or manage_own_allocations, check hierarchical access
        if (!hasPermission('view_own_allocations') && !hasPermission('manage_own_allocations')) return false

        const volunteerId = getVolunteerId()

        // Check if user is lead/deputy of this shift
        if (shift && isLeadOrDeputy(shift, volunteerId)) return true

        // Check if user is lead/deputy of parent area
        if (parentArea && isLeadOrDeputy(parentArea, volunteerId)) return true

        // Check if user is lead/deputy of grandparent event
        if (grandparentEvent && isLeadOrDeputy(grandparentEvent, volunteerId)) return true

        // For reallocation managers, they have access via event_reallocation_managers table
        // which is checked on the backend - allow view if they have manage_own_allocations
        // (the backend will validate actual access)
        if (hasPermission('manage_own_allocations') && grandparentEvent) return true

        return false
    }

    const canManageAllocations = (shift: Shift | null, parentArea?: Area | null, grandparentEvent?: Event | null): boolean => {
        if (!user) return false

        // Platform-wide permission
        if (hasPermission('manage_allocations')) return true

        // Hierarchical access requires manage_own_allocations permission
        if (!hasPermission('manage_own_allocations')) return false

        const volunteerId = getVolunteerId()

        // Check if user is lead/deputy of this shift
        if (shift && isLeadOrDeputy(shift, volunteerId)) return true

        // Check if user is lead/deputy of parent area
        if (parentArea && isLeadOrDeputy(parentArea, volunteerId)) return true

        // Check if user is lead/deputy of grandparent event
        if (grandparentEvent && isLeadOrDeputy(grandparentEvent, volunteerId)) return true

        return false
    }

    // Duty permissions (only platform-wide for now)
    const canEditDuty = (duty: any): boolean => {
        return hasPermission('manage_duties')
    }

    const canDeleteDuty = (duty: any): boolean => {
        return hasPermission('delete_duties')
    }

    const canCreateDuty = (): boolean => {
        return hasPermission('manage_duties')
    }

    // Access Color permissions (only platform-wide for now)
    const canEditAccessColor = (accessColor: any): boolean => {
        return hasPermission('manage_access_colors')
    }

    const canDeleteAccessColor = (accessColor: any): boolean => {
        return hasPermission('delete_access_colors')
    }

    const canCreateAccessColor = (): boolean => {
        return hasPermission('manage_access_colors')
    }

    // Training permissions (only platform-wide for now)
    const canEditTraining = (training: any): boolean => {
        return hasPermission('manage_trainings')
    }

    const canDeleteTraining = (training: any): boolean => {
        return hasPermission('delete_trainings')
    }

    const canCreateTraining = (): boolean => {
        return hasPermission('manage_trainings')
    }

    const canExportTraining = (): boolean => {
        return hasPermission('export_trainings')
    }

    // Preference permissions (only platform-wide)
    const canEditPreference = (preference: any): boolean => {
        return hasPermission('manage_preferences')
    }

    const canDeletePreference = (preference: any): boolean => {
        return hasPermission('delete_preferences')
    }

    const canCreatePreference = (): boolean => {
        return hasPermission('manage_preferences')
    }

    // Flag permissions (only platform-wide)
    const canEditFlag = (flag: any): boolean => {
        return hasPermission('manage_flags')
    }

    const canDeleteFlag = (flag: any): boolean => {
        return hasPermission('delete_flags')
    }

    const canCreateFlag = (): boolean => {
        return hasPermission('manage_flags')
    }

    // Announcement permissions (only platform-wide)
    const canEditAnnouncement = (announcement: any): boolean => {
        return hasPermission('manage_announcements')
    }

    const canDeleteAnnouncement = (announcement: any): boolean => {
        return hasPermission('delete_announcements')
    }

    const canCreateAnnouncement = (): boolean => {
        return hasPermission('manage_announcements')
    }


    // Visibility management (admin only)
    const canManageVisibility = (): boolean => {
        return hasPermission('manage_events')
    }

    const canDeallocateDuplicate = (): boolean => {
        return canDeallocateDuplicateRole.includes(user.role);
    }

    const canDeallocateConflict = (): boolean => {
        return canDeallocateDuplicateRole.includes(user.role);
    }

    const canChangeAllocationStatus = (): boolean => {
        return (user.permissions.includes('view_allocations') || user.permissions.includes('view_own_allocations'));
    }

    const canEditGuideline = (guideline: any): boolean => hasPermission('manage_guidelines')
    const canDeleteGuideline = (guideline: any): boolean => hasPermission('delete_guidelines')
    const canCreateGuideline = (): boolean => hasPermission('manage_guidelines')

    return {
        canEditEvent,
        canDeleteEvent,
        canCreateEvent,
        canEditArea,
        canDeleteArea,
        canCreateArea,
        canEditShift,
        canDeleteShift,
        canCreateShift,
        canViewAllocations,
        canManageAllocations,
        canEditDuty,
        canDeleteDuty,
        canCreateDuty,
        canEditAccessColor,
        canDeleteAccessColor,
        canCreateAccessColor,
        canEditTraining,
        canDeleteTraining,
        canCreateTraining,
        canEditPreference,
        canDeletePreference,
        canCreatePreference,
        canEditFlag,
        canDeleteFlag,
        canCreateFlag,
        canEditAnnouncement,
        canDeleteAnnouncement,
        canCreateAnnouncement,
        canExportTraining,
        canManageVisibility,
        canDeallocateDuplicate,
        canDeallocateConflict,
        canChangeAllocationStatus,
        canEditGuideline,
        canDeleteGuideline,
        canCreateGuideline
    }
}
